import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import "./beige-theme.css";
import { USER_GUIDE_TITLE } from './userGuideV3';
import { AuditLogCard } from './components/AuditLogCard';
import { RunDetailDrawer } from './components/RunDetailDrawer';
import { DocumentationModal } from './components/DocumentationModal';
import { BillingModal } from './components/BillingModal';
import { startShopifyConnect } from './components/ConnectStoreForm';
import { ConfigurationCard } from './components/ConfigurationCard';
import { SemicircleChart } from './components/SemicircleChart';
import { useAuth, authFetch, secureStorage, API_URL } from './hooks/useAuth';
import { useStoreData } from './hooks/useStoreData';
import { usePipeline } from './hooks/usePipeline';
import { SyncStatusSteps } from './components/SyncStatusSteps';

function App() {
  // ──────── Auth (from useAuth hook) ────────
  const auth = useAuth();
  const {
    apiKey, apiKeyLabel, isKeyRevealed, setIsKeyRevealed,
    keyLoading, copySuccess, showRegenerateConfirm, setShowRegenerateConfirm,
    userEmail, emailInput, setEmailInput,
    showEmailPrompt, setShowEmailPrompt,
    otpInput, setOtpInput, showOtpPrompt, setShowOtpPrompt,
    otpEmail, connectKeyInput, setConnectKeyInput,
    rememberMe, setRememberMe,
    showConnectKey, setShowConnectKey,
    authMessage, setAuthMessage,
    clearApiKey, validateSession, validateApiKey,
    maskKey, copyApiKey, generateApiKey, verifyOtp,
    regenerateApiKey, connectExistingKey, loginWithGoogle,
    setApiKey, setApiKeyLabel, setUserEmail,
  } = auth;

  const [githubOpen, setGithubOpen] = useState(false);
  const [shopifyOpen, setShopifyOpen] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const [githubRepo, setGithubRepo] = useState(secureStorage.getPref('github_repo'));
  const [githubToken, setGithubToken] = useState('');
  const [shopifyDomain, setShopifyDomain] = useState(secureStorage.getPref('shopify_domain'));
  const [shopifyThemeId, setShopifyThemeId] = useState(secureStorage.getPref('shopify_theme_id'));
  const [shopifyToken, setShopifyToken] = useState('');

  // Doc Modal
  const [showDocModal, setShowDocModal] = useState(false);
  const [docContent, setDocContent] = useState('');
  const [docTitle, setDocTitle] = useState('');
  const [docLoading, setDocLoading] = useState(false);

  // Credential Status
  const [credStatus, setCredStatus] = useState(null);

  // Usage
  const [usage, setUsage] = useState(null);

  // Webhook Activity
  const [webhookActivity, setWebhookActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Audit Log
  const [auditLog, setAuditLog] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);

  // Run Detail Drawer
  const [selectedRun, setSelectedRun] = useState(null);

  // ──────── Store data (from useStoreData hook) ────────
  const storeData = useStoreData(apiKey);
  const {
    stores, selectedStoreId, setSelectedStoreId,
    storesMeta, showAddStore, setShowAddStore,
    newStoreLabel, setNewStoreLabel,
    newStoreName, setNewStoreName,
    newStoreVat, setNewStoreVat,
    newStoreReg, setNewStoreReg,
    newStoreStreet, setNewStoreStreet,
    newStoreCity, setNewStoreCity,
    newStorePostcode, setNewStorePostcode,
    newStoreCountry, setNewStoreCountry,
    newStoreBilling, setNewStoreBilling,
    storeLoading, showDeleteConfirm, setShowDeleteConfirm,
    fetchStores, handleDeleteStore,
  } = storeData;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingErrorCode, setBillingErrorCode] = useState('');
  const [upgradeLoading, setUpgradeLoading] = useState(null);

  const githubRef = useRef(null);
  const shopifyRef = useRef(null);


  // ──────── On Mount: validate session via HttpOnly cookie ────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthShop = params.get('shop');
    if (oauthShop) {
      sessionStorage.setItem('dsk_shop', oauthShop.toLowerCase());
      // Arriving from Shopify OAuth: prefer the fresh OAuth cookie session over any stale Bearer key
      sessionStorage.removeItem('dsk_key');
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (params.get('billing') === 'store_taken') {
      setBillingErrorCode('store_taken');
      setBillingError("This store already pays for another account's plan. You can still add it as a managed store with your own credentials, or bill through a different store.");
      setShowUpgradeModal(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (params.get('billing') === 'store_limit') {
      setBillingErrorCode('store_limit');
      setBillingError(`Your plan allows ${params.get('limit') || 1} connected store(s). Upgrade to connect another store.`);
      setShowUpgradeModal(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (params.get('auth') === 'google_failed') {
      setAuthMessage('Google sign-in could not be completed. Please try again or use email verification.');
      window.history.replaceState(null, '', window.location.pathname);
    }
    validateSession();
    setTimeout(() => setInitialLoadDone(true), 500);
  }, [validateSession, setAuthMessage]);

  const fetchCredentialStatus = useCallback(async () => {
    if (!apiKey) return;
    try {
      const res = await authFetch(`${API_URL}/api/credentials/status`);
      if (res.ok) setCredStatus(await res.json());
    } catch (err) { console.error('Failed to fetch credential status:', err); }
  }, [apiKey]);

  const fetchUsage = useCallback(async () => {
    if (!apiKey) return;
    try {
      const res = await authFetch(`${API_URL}/api/usage`);
      if (res.ok) setUsage(await res.json());
    } catch (err) { console.error('Failed to fetch usage:', err); }
  }, [apiKey]);

  const fetchWebhookActivity = useCallback(async () => {
    if (!apiKey) return;
    setActivityLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/webhook-activity`);
      if (res.ok) {
        const d = await res.json();
        const events = (d.events || []).map(e => ({
          ...e,
          received_at: e.received_at || e.created_at,
          dry_run: e.action === 'preview_sync'
        }));
        setWebhookActivity(events);
      }
    } catch (err) { console.error('Failed to fetch webhook activity:', err); } finally { setActivityLoading(false); }
  }, [apiKey]);

  const fetchAuditLog = useCallback(async (page = 0) => {
    if (!apiKey) return;
    setAuditLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/audit?limit=20&offset=${page * 20}`);
      if (res.ok) {
        const d = await res.json();
        setAuditLog(d.runs || []);
        setAuditTotal(d.total || 0);
        setAuditPage(page);
      }
    } catch (err) { console.error('Failed to fetch audit log:', err); } finally { setAuditLoading(false); }
  }, [apiKey]);

  // ──────── Pipeline / Sync (from usePipeline hook) ────────
  const pipeline = usePipeline(apiKey, selectedStoreId, fetchUsage, fetchWebhookActivity);
  const {
    syncStatus, syncMessage, setSyncMessage,
    previewOnly, setPreviewOnly,
    pipelineStages, currentStage, totalStages,
    lastRunId, lastRunDryRun, lastRun,
    tokenPreview, tokenPreviewOpen, setTokenPreviewOpen,
    triggerSync, resetSync, closeEventSource,
  } = pipeline;
  const syncSteps = syncStatus === 'syncing'
    ? { status: 'syncing', stages: pipelineStages, errorMessage: null, finishedAt: null, prNumber: null }
    : lastRun
      ? { status: lastRun.status, stages: lastRun.stages, errorMessage: lastRun.error, finishedAt: lastRun.finishedAt, prNumber: lastRun.prNumber }
      : { status: 'idle', stages: [], errorMessage: null, finishedAt: null, prNumber: null };

  const handleRollback = async (runId) => {
    if (!window.confirm('Are you sure you want to rollback this deployment? This will restore the previous tokens.css on Shopify.')) return;
    try {
      const res = await authFetch(`${API_URL}/api/pipeline/rollback/${runId}`, {
        method: 'POST'
      });
      if (res.ok) {
        const d = await res.json();
        alert(d.message || 'Rollback complete');
        fetchAuditLog(auditPage);
        fetchWebhookActivity();
      } else {
        const e = await res.json();
        alert(e.error || 'Rollback failed');
      }
    } catch (e) { alert('Rollback failed: ' + e.message); }
  };

  const handleExportCsv = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/audit/export`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'designsync-audit-log.csv'; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) { console.error('CSV export failed:', err); }
  };

  // ──────── Fetch all data when authenticated ────────
  useEffect(() => {
    if (apiKey) {
      fetchCredentialStatus();
      fetchUsage();
      fetchWebhookActivity();
      fetchStores();
      fetchAuditLog();
      // Resume the subscription flow started before the Shopify OAuth round-trip
      if (sessionStorage.getItem('dsk_resume_billing') === '1') {
        sessionStorage.removeItem('dsk_resume_billing');
        setShowUpgradeModal(true);
      }
    }
  }, [apiKey, fetchCredentialStatus, fetchUsage, fetchWebhookActivity, fetchStores, fetchAuditLog]);

  const handleAddStore = async () => {
    const result = await storeData.handleAddStore((msg) => setSyncMessage(msg));
    if (result.success) fetchUsage();
  };

  const handleDeleteStoreAndRefresh = async (storeId) => {
    const result = await handleDeleteStore(storeId);
    if (result.success) fetchUsage();
  };


  const handleUpgrade = async (tier, interval = 'monthly', shop) => {
    setUpgradeLoading(tier);
    setBillingError(''); setBillingErrorCode('');
    try {
      // The server bills the OAuth store of the authenticated session — no client-side store selection
      const res = await authFetch(`${API_URL}/api/billing/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: tier, interval, shop }),
      });
      const data = await res.json();
      if (data.success && data.confirmation_url) {
        setShowUpgradeModal(false);
        window.location.href = data.confirmation_url;
        return;
      }
      // Keep the modal open and show the reason — never bounce silently back to the dashboard
      setBillingErrorCode(data.detail || '');
      setBillingError(data.error || 'Failed to start billing. Please connect your Shopify store first via the Shopify admin.');
    } catch (err) {
      setBillingError('Billing request failed. Please try again.');
    } finally {
      setUpgradeLoading(null);
    }
  };

  // ──────── Credential save on toggle close ────────
  useEffect(() => {
    if (!githubOpen && initialLoadDone) {
      secureStorage.setPref('github_repo', githubRepo);
      // GitHub token stored server-side only (via saveCredentialsToDb)
      if (apiKey && (githubRepo || githubToken)) saveCredentialsToDb();
    }
  }, [githubOpen]); // Intentionally only triggers on panel close

  useEffect(() => {
    if (!shopifyOpen && initialLoadDone) {
      secureStorage.setPref('shopify_domain', shopifyDomain);
      secureStorage.setPref('shopify_theme_id', shopifyThemeId);
      // Shopify token stored server-side only (via saveCredentialsToDb)
      if (apiKey && (shopifyDomain || shopifyToken)) saveCredentialsToDb();
    }
  }, [shopifyOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (githubRef.current && !githubRef.current.contains(e.target)) setGithubOpen(false);
      if (shopifyRef.current && !shopifyRef.current.contains(e.target)) setShopifyOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cleanup SSE on unmount
  useEffect(() => closeEventSource, [closeEventSource]);

  const loadDoc = async (type) => {
    setDocLoading(true); setShowDocModal(true);
    if (type === 'readme') {
      setDocTitle(USER_GUIDE_TITLE);
      setDocContent('__USER_GUIDE__');
      setDocLoading(false);
      return;
    }
    try {
      const ep = '/api/docs/webhooks';
      const res = await fetch(`${API_URL}${ep}`);
      if (res.ok) { const d = await res.json(); setDocTitle(d.title); setDocContent(d.content); }
      else { setDocTitle('Error'); setDocContent('Failed to load.'); }
    } catch (err) { console.error('Doc fetch failed:', err); setDocTitle('Error'); setDocContent('Failed to load.'); }
    finally { setDocLoading(false); }
  };

  const renderMd = (md) => {
    if (!md) return '';
    return md
      .replace(/^### (.*$)/gim, '<h3 style="font-size:16px;font-weight:600;margin:16px 0 8px">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 style="font-size:18px;font-weight:700;margin:20px 0 10px">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 style="font-size:22px;font-weight:700;margin:24px 0 12px">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre style="background:#d5d0be;padding:12px;border-radius:6px;margin:12px 0;overflow-x:auto;font-size:12px"><code>$2</code></pre>')
      .replace(/`([^`]+)`/g, '<code style="background:#d5d0be;padding:2px 6px;border-radius:3px;font-size:12px">$1</code>')
      .replace(/^\- (.*$)/gim, '<li style="margin-left:16px;margin-bottom:4px">$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:#4a6ca0;text-decoration:underline">$1</a>')
      .replace(/^---$/gim, '<hr style="border-color:#c1bba6;margin:16px 0" />')
      .replace(/\n\n/g, '</p><p style="margin-bottom:12px">')
      .replace(/\n/g, '<br />');
  };

  const saveCredentialsToDb = async () => {
    if (!apiKey) return;
    try {
      await authFetch(`${API_URL}/api/credentials/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shopify_store_url: shopifyDomain, shopify_token: shopifyToken, shopify_theme_id: shopifyThemeId, github_repo: githubRepo, github_pat: githubToken })
      });
      fetchCredentialStatus();
    } catch (err) { console.error('Failed to save credentials:', err); }
  };

  // ──────── Render ────────
  return (
    <div style={{minHeight:'100vh',background:'var(--bg-main)',color:'var(--text-primary)'}}>
      <div style={{maxWidth:'720px',margin:'0 auto',padding:'24px 20px'}}>

        {/* Header */}
        <div className="beige-header" data-testid="header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }} data-testid="header-brand">
            <img
              src="/designsync-logo.png"
              alt="DesignSync"
              style={{ height: '52px', width: 'auto' }}
              data-testid="header-logo"
            />
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '700', fontSize: '24px' }} data-testid="header-title">
              DesignSync Agent
            </span>
          </div>
        </div>

        {/* Usage Card — Premium Chrome Bezel */}
        <div className="metal-card" data-testid="usage-card">
          <div className="metal-card-inner">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
              <div className="beige-card-title" style={{marginBottom:0}}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                Usage
              </div>
              {usage && (
                <span className="metal-badge" data-testid="tier-badge">{usage.tier || 'free'}</span>
              )}
            </div>
            {usage ? (
              <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'13px'}}>
                  <span>Runs this month</span>
                  <span style={{fontWeight:700}}>{usage.runs_used || 0}/{usage.runs_limit || 5}</span>
                </div>
                <div className="metal-progress-track" data-testid="progress-track">
                  <div className="metal-progress-fill" style={{width: `${Math.min(((usage.runs_used || 0) / (usage.runs_limit || 5)) * 100, 100)}%`}} />
                  <div className="metal-progress-ticks" style={{
                    backgroundImage: `repeating-linear-gradient(90deg, transparent 0, transparent calc(${100 / (usage.runs_limit || 5)}% - 1px), rgba(0,0,0,0.18) calc(${100 / (usage.runs_limit || 5)}% - 1px), rgba(0,0,0,0.18) ${100 / (usage.runs_limit || 5)}%)`
                  }} />
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'12px',color:'var(--text-muted)'}}>
                  <span>Stores: {storesMeta.stores_used}/{storesMeta.stores_limit}</span>
                  <span>Resets in ~{usage.days_until_reset || 24} days</span>
                </div>
                <div data-testid="billing-store-badge" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'8px',fontSize:'12px',color:'var(--text-muted)',flexWrap:'wrap'}}>
                  {usage.oauth_store_domain ? (
                    <>
                      <span>Billing store: <strong data-testid="billing-store-domain" style={{color:'var(--text-primary)'}}>{usage.oauth_store_domain}</strong></span>
                      <span style={{display:'inline-flex',gap:'10px'}}>
                        <button type="button" className="link-btn" onClick={() => setShowUpgradeModal(true)} data-testid="change-store-btn">Change</button>
                        <button type="button" className="link-btn" onClick={() => startShopifyConnect(usage.oauth_store_domain)} data-testid="reconnect-store-btn">Reconnect</button>
                      </span>
                    </>
                  ) : (
                    <>
                      <span data-testid="billing-store-none">No billing store connected</span>
                      <button type="button" className="link-btn" onClick={() => setShowUpgradeModal(true)} data-testid="connect-store-link">Connect Shopify store</button>
                    </>
                  )}
                </div>
                {(usage.tier === 'free') && (
                  <button className="metal-btn" onClick={() => setShowUpgradeModal(true)} data-testid="upgrade-btn" style={{marginTop:'4px'}}>Upgrade Plan</button>
                )}
                {usage.tier !== 'free' && usage.tier !== 'unlimited' && usage.tier !== 'lifetime' && (
                  <button className="metal-btn" onClick={() => setShowUpgradeModal(true)} data-testid="change-plan-btn" style={{marginTop:'4px'}}>Change Plan</button>
                )}
              </div>
            ) : (
              <div style={{fontSize:'13px',color:'var(--text-muted)'}}>Connect API key to view usage</div>
            )}
          </div>
        </div>

        {/* ═══════ Configuration Card ═══════ */}
        <ConfigurationCard
          githubOpen={githubOpen} setGithubOpen={setGithubOpen}
          shopifyOpen={shopifyOpen} setShopifyOpen={setShopifyOpen}
          githubRepo={githubRepo} setGithubRepo={setGithubRepo}
          githubToken={githubToken} setGithubToken={setGithubToken}
          shopifyDomain={shopifyDomain} setShopifyDomain={setShopifyDomain}
          shopifyThemeId={shopifyThemeId} setShopifyThemeId={setShopifyThemeId}
          shopifyToken={shopifyToken} setShopifyToken={setShopifyToken}
          credStatus={credStatus} githubRef={githubRef} shopifyRef={shopifyRef}
        />

        {/* ═══════ API Key Card — Chrome Bezel ═══════ */}
        <div className="metal-card" data-testid="api-key-card">
          <div className="metal-card-inner">
          <div className="beige-card-title">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
            API Key
          </div>
          {apiKey ? (
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              <div className="key-display" data-testid="api-key-display">
                <span style={{color:'var(--text-muted)',fontSize:'12px'}}>{apiKey === 'session' ? 'Authenticated via session' : 'Dashboard key'}</span>
                <div style={{marginTop:'4px',letterSpacing:'1px'}}>{apiKey === 'session' ? '● Active Session' : (isKeyRevealed ? apiKey : maskKey(apiKey))}</div>
              </div>
              <div style={{display:'flex',gap:'8px'}}>
                {apiKey !== 'session' && <button className="metal-plate-btn" onClick={copyApiKey} data-testid="copy-key-btn">{copySuccess ? 'Copied!' : 'Copy Key'}</button>}
                <button className="metal-plate-btn" onClick={() => setShowRegenerateConfirm(true)} data-testid="regenerate-btn">Regenerate</button>
                <button className="metal-plate-btn" onClick={clearApiKey} data-testid="logout-btn">Logout</button>
              </div>
              {showRegenerateConfirm && (
                <div className="animate-slideDown" style={{padding:'10px',background:'rgba(200,170,160,0.3)',border:'1px solid #c4a69a',borderRadius:'6px'}}>
                  <p style={{fontSize:'12px',marginBottom:'8px'}}>This will invalidate the current key.</p>
                  <div style={{display:'flex',gap:'8px'}}>
                    <button className="metal-btn" style={{background:'linear-gradient(180deg, #d47068, #b8504a, #983830)',color:'#fff',textShadow:'0 1px 2px rgba(0,0,0,0.3)'}} onClick={regenerateApiKey} data-testid="confirm-regenerate-btn">{keyLoading ? 'Working...' : 'Confirm'}</button>
                    <button className="metal-btn" onClick={() => setShowRegenerateConfirm(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <p style={{fontSize:'13px',color:'var(--text-muted)'}}>Generate or connect an API key to access the dashboard.</p>
              {showEmailPrompt ? (
                <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                  <input className="beige-input" value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="your@email.com" data-testid="email-input" />
                  <button className="metal-btn" onClick={() => generateApiKey(emailInput)} disabled={!emailInput || keyLoading} data-testid="confirm-generate-btn">{keyLoading ? 'Sending...' : 'Send Verification Code'}</button>
                </div>
              ) : showOtpPrompt ? (
                <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                  <p style={{fontSize:'12px',color:'var(--text-muted)'}}>Enter the 6-digit code sent to <strong>{otpEmail}</strong></p>
                  <input className="beige-input" value={otpInput} onChange={e => setOtpInput(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="000000" maxLength={6} style={{letterSpacing:'4px',textAlign:'center',fontSize:'18px',fontFamily:'monospace'}} data-testid="otp-input" />
                  <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',color:'var(--text-muted)',cursor:'pointer',userSelect:'none'}}>
                    <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} data-testid="remember-me-checkbox" style={{accentColor:'var(--accent)'}} />
                    Remember me for 30 days
                  </label>
                  <button className="metal-btn" onClick={verifyOtp} disabled={otpInput.length !== 6 || keyLoading} data-testid="verify-otp-btn">{keyLoading ? 'Verifying...' : 'Verify & Login'}</button>
                  <button className="metal-btn" onClick={() => { setShowOtpPrompt(false); setShowEmailPrompt(true); setOtpInput(''); setSyncMessage(''); }} style={{fontSize:'12px'}}>Back</button>
                </div>
              ) : showConnectKey ? (
                <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                  <input className="beige-input" value={connectKeyInput} onChange={e => setConnectKeyInput(e.target.value)} placeholder="dsk_live_..." data-testid="deploy-connect-key-input" />
                  <button className="metal-btn" onClick={connectExistingKey} disabled={!connectKeyInput || keyLoading} data-testid="deploy-connect-btn">{keyLoading ? 'Connecting...' : 'Connect'}</button>
                  <button className="metal-btn" onClick={() => setShowConnectKey(false)} style={{fontSize:'12px'}}>Back</button>
                </div>
              ) : (
                <div style={{display:'flex',gap:'10px'}}>
                  <button className="metal-plate-btn" onClick={() => setShowEmailPrompt(true)} data-testid="generate-key-btn">Generate New Key</button>
                  <button className="metal-plate-btn" onClick={() => setShowConnectKey(true)} data-testid="connect-existing-btn">Connect Existing</button>
                </div>
              )}
              {!showOtpPrompt && !showConnectKey && (
                <>
                  <div className="auth-divider" data-testid="auth-or-divider"><span>or</span></div>
                  <button className="metal-plate-btn google-signin-btn" onClick={loginWithGoogle} data-testid="google-signin-btn">
                    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.9 6.1C12.3 13.4 17.7 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/>
                      <path fill="#FBBC05" d="M10.4 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.7l7.9-6.1z"/>
                      <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.7-3.9-13.6-9.4l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
                    </svg>
                    Continue with Google
                  </button>
                </>
              )}
              {(authMessage || syncMessage) && <p style={{fontSize:'12px',color:'var(--accent-red)'}}>{authMessage || syncMessage}</p>}
            </div>
          )}
          </div>
        </div>

        {/* ═══════ Target Store / Deploy Card — Chrome Bezel ═══════ */}
        <div className="metal-card" data-testid="deploy-card">
          <div className="metal-card-inner">
          <div className="beige-card-title">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            Target Store
            <span style={{marginLeft:'auto',fontSize:'11px',color:'var(--text-muted)',fontWeight:400,fontStyle:'italic'}} data-testid="store-limit-indicator">optional — uses Configuration credentials by default</span>
          </div>

          {/* Store selector */}
          {stores.length > 0 ? (
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              <select className="beige-select" value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)} data-testid="store-dropdown">
                <option value="">Use Configuration credentials</option>
                {stores.map(s => <option key={s.id} value={s.id}>Store: {s.store_label}{s.shopify_store_url ? ` (${s.shopify_store_url})` : ''}</option>)}
              </select>
              <div style={{display:'flex',gap:'8px'}}>
                <button className="metal-btn" style={{flex:1}} onClick={() => storesMeta.can_add ? setShowAddStore(true) : null} disabled={!storesMeta.can_add} data-testid="add-store-btn">Add Store</button>
                {selectedStoreId && (
                  <button className="metal-btn" onClick={() => setShowDeleteConfirm(selectedStoreId)} data-testid="delete-store-btn" style={{padding:'8px 14px',width:'auto'}}>
                    <svg style={{width:'14px',height:'14px'}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                )}
              </div>
              {!storesMeta.can_add && <p style={{fontSize:'11px',color:'var(--accent-red)'}}>Store limit reached. Upgrade to add more.</p>}
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'8px 0',gap:'10px'}}>
              <p style={{fontSize:'13px',color:'var(--text-muted)'}}>No additional stores. Sync Now uses your Configuration credentials.</p>
              <button className="metal-btn" style={{width:'auto',padding:'8px 24px'}} onClick={() => setShowAddStore(true)} data-testid="add-first-store-btn">Add Store</button>
            </div>
          )}

          {/* Delete confirmation */}
          {showDeleteConfirm && (
            <div className="animate-slideDown" style={{padding:'10px',background:'rgba(200,170,160,0.3)',border:'1px solid #c4a69a',borderRadius:'6px',marginTop:'8px'}} data-testid="delete-store-confirm">
              <p style={{fontSize:'12px',marginBottom:'8px'}}>Remove this store?</p>
              <div style={{display:'flex',gap:'8px'}}>
                <button className="metal-btn" style={{flex:1,background:'linear-gradient(180deg, #d47068, #b8504a, #983830)',color:'#fff',textShadow:'0 1px 2px rgba(0,0,0,0.3)'}} onClick={() => handleDeleteStoreAndRefresh(showDeleteConfirm)} data-testid="confirm-delete-store-btn">Remove</button>
                <button className="metal-btn" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Add Store Form */}
          {showAddStore && (
            <div className="add-store-form animate-slideDown" style={{marginTop:'10px'}} data-testid="add-store-form">
              <div style={{fontSize:'13px',fontWeight:600,marginBottom:'10px'}}>New Store</div>
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                <input className="beige-input" value={newStoreLabel} onChange={e => setNewStoreLabel(e.target.value)} placeholder="e.g. Production" data-testid="new-store-label-input" />
                <input className="beige-input" value={newStoreName} onChange={e => setNewStoreName(e.target.value)} placeholder="Legal store / business name" data-testid="new-store-name-input" />
                <input className="beige-input" value={newStoreVat} onChange={e => setNewStoreVat(e.target.value)} placeholder="VAT number (optional)" data-testid="new-store-vat-input" />
                <input className="beige-input" value={newStoreReg} onChange={e => setNewStoreReg(e.target.value)} placeholder="Company reg. number (optional)" data-testid="new-store-reg-input" />
                <input className="beige-input" value={newStoreStreet} onChange={e => setNewStoreStreet(e.target.value)} placeholder="Street address" data-testid="new-store-street-input" />
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px'}}>
                  <input className="beige-input" value={newStoreCity} onChange={e => setNewStoreCity(e.target.value)} placeholder="City" data-testid="new-store-city-input" />
                  <input className="beige-input" value={newStorePostcode} onChange={e => setNewStorePostcode(e.target.value)} placeholder="Postcode" data-testid="new-store-postcode-input" />
                  <input className="beige-input" value={newStoreCountry} onChange={e => setNewStoreCountry(e.target.value)} placeholder="Country" data-testid="new-store-country-input" />
                </div>
                <input className="beige-input" value={newStoreBilling} onChange={e => setNewStoreBilling(e.target.value)} placeholder="Billing account or IBAN (optional)" data-testid="new-store-billing-input" />
                <div style={{display:'flex',gap:'10px',marginTop:'4px'}}>
                  <button className="metal-plate-btn" onClick={() => setShowAddStore(false)}>Cancel</button>
                  <button className="metal-plate-btn" onClick={handleAddStore} disabled={!newStoreLabel || storeLoading} data-testid="confirm-add-store-btn">{storeLoading ? 'Adding...' : 'Add Store'}</button>
                </div>
              </div>
            </div>
          )}

          {/* SYNC NOW Button */}
          <div style={{marginTop:'16px'}}>
            <div className="sync-now-container">
              <div className="sync-now-glow" />
              <button
                className="sync-now-btn"
                onClick={triggerSync}
                disabled={syncStatus === 'syncing' || !apiKey || (usage?.over_limit && !previewOnly)}
                data-testid="sync-now-btn"
              >
                {syncStatus === 'syncing' ? (
                  <>{currentStage}/{totalStages || '...'}<br/><span style={{fontSize:'11px',fontWeight:400}}>SYNCING</span></>
                ) : syncStatus === 'success' ? (
                  <><span style={{fontSize:'14px'}}>DONE</span></>
                ) : syncStatus === 'failed' ? (
                  <><span style={{fontSize:'14px'}}>FAILED</span></>
                ) : (
                  <>SYNC<br/>NOW</>
                )}
              </button>
            </div>

            {/* Toggle — single clear control with ON/OFF labels */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'10px',marginTop:'10px'}}>
              <span style={{fontSize:'12px',fontWeight:600,color: previewOnly ? 'var(--text-muted)' : 'var(--text-primary)'}}>Live</span>
              <div className={`metal-toggle ${previewOnly ? 'active' : ''}`} onClick={() => setPreviewOnly(!previewOnly)} data-testid="preview-toggle">
                <span className="metal-toggle-label metal-toggle-label-on">ON</span>
                <span className="metal-toggle-label metal-toggle-label-off">OFF</span>
                <div className={`metal-toggle-knob ${previewOnly ? 'on' : 'off'}`}>
                  <div className={`metal-toggle-knob-dot ${previewOnly ? 'active' : 'inactive'}`} />
                </div>
              </div>
              <span style={{fontSize:'12px',fontWeight:600,color: previewOnly ? 'var(--text-primary)' : 'var(--text-muted)'}}>Safe Preview</span>
            </div>

            {/* Sync feedback */}
            {syncMessage && syncStatus !== 'syncing' && (
              <p style={{textAlign:'center',fontSize:'12px',marginTop:'8px',color: syncStatus === 'success' ? 'var(--accent-green)' : syncStatus === 'failed' ? 'var(--accent-red)' : 'var(--text-secondary)'}} data-testid="sync-message">{syncMessage}</p>
            )}

            {/* Result badge — Safe Preview vs Live Deploy */}
            {syncStatus === 'success' && lastRunDryRun && (
              <div data-testid="result-badge-preview" style={{
                margin:'10px 0',padding:'10px 14px',borderRadius:'8px',fontSize:'12px',fontWeight:600,
                border:'1.5px solid #3b82f6',background:'rgba(59,130,246,0.08)',color:'#2563eb',textAlign:'center'
              }}>SAFE PREVIEW COMPLETE — Tokens validated. No changes were deployed to Shopify.</div>
            )}
            {syncStatus === 'success' && !lastRunDryRun && (
              <div data-testid="result-badge-live" style={{
                margin:'10px 0',padding:'10px 14px',borderRadius:'8px',fontSize:'12px',fontWeight:600,
                border:'1.5px solid #16a34a',background:'rgba(22,163,74,0.08)',color:'#15803d',textAlign:'center'
              }}>DEPLOYED SUCCESSFULLY — tokens.css is live on Shopify.</div>
            )}

            {/* Watermark warning for free-tier live deploy */}
            {syncStatus === 'success' && !lastRunDryRun && usage?.tier === 'free' && (
              <div data-testid="watermark-warning" onClick={() => setShowUpgradeModal(true)} style={{
                margin:'6px 0 10px',padding:'10px 14px',borderRadius:'8px',fontSize:'12px',fontWeight:600,cursor:'pointer',
                border:'1.5px solid #d97706',background:'rgba(217,119,6,0.08)',color:'#b45309',textAlign:'center'
              }}>Deployed with free-tier watermark. Upgrade to remove it from tokens.css.</div>
            )}

            {/* Token preview panel — after Safe Preview */}
            {syncStatus === 'success' && lastRunDryRun && tokenPreview && tokenPreview.length > 0 && (
              <div style={{margin:'10px 0'}} data-testid="token-preview-panel">
                <button className="metal-btn" onClick={() => setTokenPreviewOpen(!tokenPreviewOpen)} style={{fontSize:'12px',width:'100%',textAlign:'left',padding:'8px 14px'}}>
                  {tokenPreviewOpen ? 'Hide' : 'Preview'} Extracted Tokens ({tokenPreview.length})
                </button>
                {tokenPreviewOpen && (
                  <pre style={{
                    marginTop:'8px',padding:'12px',borderRadius:'8px',fontSize:'11px',lineHeight:'1.6',
                    background:'#1e1e1e',color:'#d4d4d4',overflow:'auto',maxHeight:'200px',
                    fontFamily:"'SF Mono','Fira Code',monospace",border:'1px solid #333'
                  }}>
{tokenPreview.map(t => `${t.name}: ${t.value};`).join('\n')}
                  </pre>
                )}
              </div>
            )}

            {/* Reset */}
            {(syncStatus === 'success' || syncStatus === 'failed') && (
              <div style={{textAlign:'center',marginTop:'8px'}}>
                <button className="metal-btn" onClick={resetSync} data-testid="reset-sync-btn" style={{fontSize:'12px',width:'auto'}}>Run again</button>
              </div>
            )}

            {/* Sync status steps — always visible; keeps the last result until the next run starts */}
            <SyncStatusSteps {...syncSteps} githubRepo={githubRepo} />
          </div>
          </div>
        </div>

        {/* ═══════ Webhook Activity Card ═══════ */}
        {/* ═══════ Webhook Activity Card — Chrome Bezel ═══════ */}
        <div className="metal-card" data-testid="webhook-card">
          <div className="metal-card-inner">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
            <div className="beige-card-title" style={{marginBottom:0}}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{color:'#c8a050'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              Webhook Activity
            </div>
            <button className="metal-btn" onClick={() => fetchWebhookActivity()} disabled={activityLoading} style={{fontSize:'12px',padding:'5px 14px',width:'auto'}} data-testid="webhook-refresh-btn">
              {activityLoading ? '...' : 'Refresh'}
            </button>
          </div>
          <div style={{fontSize:'13px',color:'#8a8580',fontStyle:'italic',marginBottom:'10px',paddingLeft:'28px'}} data-testid="webhook-repo-name">
            {(() => {
              const evts = webhookActivity.length > 0 ? webhookActivity : [{repo:'org/tokens'}];
              const repo = evts[0]?.repository || evts[0]?.repo || '';
              return repo ? repo : 'No activity yet';
            })()}
          </div>
          <SemicircleChart events={webhookActivity.length > 0 ? webhookActivity : [{repo:'org/tokens',status:'success',dry_run:false,received_at:'2026-03-30T12:35:00Z'},{repo:'org/assets',status:'error',dry_run:true,received_at:'2026-03-29T11:22:00Z'},{repo:'org/theme',status:'deployed',dry_run:false,received_at:'2026-03-28T10:15:00Z'},{repo:'org/cards',status:'success',dry_run:true,received_at:'2026-03-27T09:48:00Z'},{repo:'org/mobile',status:'success',dry_run:false,received_at:'2026-03-26T08:30:00Z'},{repo:'org/styles',status:'success',dry_run:false,received_at:'2026-03-25T07:15:00Z'}]} />
          </div>
        </div>


        {/* ═══════ Audit Log Card ═══════ */}
        <AuditLogCard
          auditOpen={auditOpen} setAuditOpen={setAuditOpen}
          auditLog={auditLog} auditTotal={auditTotal} auditPage={auditPage}
          auditLoading={auditLoading} fetchAuditLog={fetchAuditLog}
          handleExportCsv={handleExportCsv} handleRollback={handleRollback}
          onSelectRun={setSelectedRun}
        />

        {/* ═══════ Run Detail Drawer ═══════ */}
        {selectedRun && (
          <RunDetailDrawer
            run={selectedRun}
            onClose={() => setSelectedRun(null)}
            webhookEvents={webhookActivity}
          />
        )}

        {/* ═══════ Documentation Card — Chrome Bezel ═══════ */}
        <div className="metal-card" data-testid="docs-card">
          <div className="metal-card-inner">
          <div className="beige-card-title">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
            Documentation
          </div>
          <div style={{display:'flex',gap:'10px'}}>
            <button className="metal-plate-btn" onClick={() => loadDoc('readme')} data-testid="doc-main-btn">Main Documentation</button>
            <button className="metal-plate-btn" onClick={() => loadDoc('webhooks')} data-testid="doc-webhook-btn">Webhook Integration</button>
          </div>
          </div>
        </div>

        {/* ═══════ Upgrade Modal ═══════ */}
        <BillingModal
          showUpgradeModal={showUpgradeModal} setShowUpgradeModal={(v) => { setShowUpgradeModal(v); if (!v) { setBillingError(''); setBillingErrorCode(''); } }}
          handleUpgrade={handleUpgrade} upgradeLoading={upgradeLoading} usage={usage} billingError={billingError} billingErrorCode={billingErrorCode} onStoreChange={fetchUsage}
        />

        {/* ═══════ Doc Modal ═══════ */}
        <DocumentationModal
          showDocModal={showDocModal} setShowDocModal={setShowDocModal}
          docTitle={docTitle} docContent={docContent} docLoading={docLoading}
          renderMd={renderMd}
        />

        {/* Footer */}
        <div className="beige-footer" data-testid="footer">
          <span>© {new Date().getFullYear()} MBOSS.US · DesignSync Agent v1.0.0</span>
          <span className="beige-footer-links">
            <a href="/privacy-policy" data-testid="footer-privacy-link">Privacy Policy</a>
            <span aria-hidden="true">·</span>
            <a href="/terms-of-service" data-testid="footer-terms-link">Terms of Service</a>
            <span aria-hidden="true">·</span>
            <a href="/development-atlas" data-testid="footer-atlas-link">Development Atlas</a>
            <span aria-hidden="true">·</span>
            <a href="/designsync-setup" data-testid="footer-setup-link">Setup Guide</a>
          </span>
        </div>
      </div>
    </div>
  );
}

export default App;
